import { IntegrationLaunchSource } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useIsFhirAuth } from '@/hooks/integrations/useIsFhirAuth';
import { externalLaunchContextAtom } from '@heidi/integrations/ehr/data/atoms';
import { type EHRProvider } from '@heidi/schema/ml-scribe';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

export const useIntegrationLaunchSource = ({
  hasIntegrationOnboardingSession,
  hasLegacyEhrProfile,
}: {
  hasIntegrationOnboardingSession: boolean;
  hasLegacyEhrProfile: boolean;
}) => {
  const externalLaunchContext = useAtomValue(externalLaunchContextAtom);
  const isFhirAuth = useIsFhirAuth();

  const genericLaunchEhrProvider = externalLaunchContext?.ehrProvider as
    | EHRProvider
    | undefined;

  const integrationLaunchSource = useMemo(() => {
    /* Check for generic EHR launch first since isFhirAuth is also set */
    if (genericLaunchEhrProvider) {
      return IntegrationLaunchSource.GENERIC_EHR_LAUNCH;
    }

    if (hasIntegrationOnboardingSession) {
      return IntegrationLaunchSource.INTEGRATION_ONBOARDING_SESSION;
    }

    if (hasLegacyEhrProfile) {
      return IntegrationLaunchSource.LEGACY_EHR_PROFILE;
    }

    if (isFhirAuth) {
      return IntegrationLaunchSource.LEGACY_EPIC_FHIR_AUTH;
    }

    return IntegrationLaunchSource.NONE;
  }, [
    hasIntegrationOnboardingSession,
    hasLegacyEhrProfile,
    isFhirAuth,
    genericLaunchEhrProvider,
  ]);

  const isLegacyEpicFhirAuth =
    integrationLaunchSource === IntegrationLaunchSource.LEGACY_EPIC_FHIR_AUTH;

  return {
    integrationLaunchSource,
    isLegacyEpicFhirAuth,
    isGenericEhrLaunch: Boolean(externalLaunchContext?.ehrProvider),
    genericLaunchEhrProvider,
  };
};
